"use client"

import { createContainer } from "unstated-next"
import { useState } from "react"

import { useCreateCanvas } from "./CreateCanvas"

import { FEMALE_PRESETS, PresetType } from "../config"

import { useUser } from "@/app/actions/user"
import toast from "react-hot-toast"
import Error from "next/error"

export const toBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.readAsDataURL(blob)
  })
}

const CreateGenerationContainer = createContainer(() => {
  const { userData, fetchUserData } = useUser()
  const { canvasRef, resetCanvas, isMasked, setIsMasked } = useCreateCanvas()

  const [selectedPreset, setSelectedPreset] = useState<PresetType | null>(null)
  const [selectedPresets, setSelectedPresets] = useState<
    Record<string, PresetType>
  >({})
  const [userPrompt, setUserPrompt] = useState("")
  const [inputImageBase64, setInputImageBase64] = useState<string | null>(null)
  const [inputImageName, setInputImageName] = useState<string>("Untitled")
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 0,
    height: 0,
  })
  const [outputImageBase64, setOutputImageBase64] = useState<string | null>(
    null
  )
  const [isCustomFaceMode, setIsCustomFaceMode] = useState(false)
  const [customFaceImageBase64, setCustomFaceImageBase64] = useState<
    string | null
  >(null)
  const [customFaceImageBase64Name, setCustomFaceImageBase64Name] = useState<
    string | null
  >(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [generationTime, setGenerationTime] = useState<number>(0)
  const [showHowToUse, setShowHowToUse] = useState<boolean>(false)

  const updatePreset = (preset: PresetType) => {
    if (preset.subcategory.toLowerCase() === "breasts features") {
      const isAlreadySelected = selectedPresets[
        preset.subcategory
      ]?.value.includes(preset.value)

      let newValue = ""

      if (isAlreadySelected) {
        const values =
          selectedPresets[preset.subcategory]?.value.split(", ") || []
        const filteredValues = values.filter((val) => val !== preset.value)
        newValue = filteredValues.join(", ")
      } else {
        const currentValues =
          selectedPresets[preset.subcategory]?.value.split(", ") || []

        if (currentValues.length >= 5 && currentValues[0] !== "") {
          toast.error("You can select up to 5 breast features")
          return
        }

        const newValues = [
          ...currentValues.filter((v) => v !== ""),
          preset.value,
        ]
        newValue = newValues.join(", ")
      }

      if (newValue) {
        const combinedPreset: PresetType = {
          ...preset,
          value: newValue,
          label: "Multiple Breast Features",
        }

        setSelectedPresets((prev) => ({
          ...prev,
          [preset.subcategory]: combinedPreset,
        }))
      } else {
        const newSelectedPresets = { ...selectedPresets }
        delete newSelectedPresets[preset.subcategory]
        setSelectedPresets(newSelectedPresets)
      }
    } else {
      setSelectedPresets((prev) => ({
        ...prev,
        [preset.subcategory]: preset,
      }))
    }

    setSelectedPreset(preset)
  }

  const clearSelectedPresets = () => {
    setSelectedPresets({})
    setSelectedPreset(null)
  }

  const getBase64InpaintMask = () => {
    const brushCanvas = canvasRef?.current
    if (!brushCanvas) {
      return
    }

    const cnvs = document.createElement("canvas")
    cnvs.width = imageNaturalSize.width
    cnvs.height = imageNaturalSize.height

    const ctx = cnvs.getContext("2d")
    if (!ctx) {
      return
    }
    ctx.imageSmoothingQuality = "high"
    ctx.imageSmoothingEnabled = true

    ctx.fillStyle = "white"
    ctx.fillRect(0, 0, cnvs.width, cnvs.height)
    ctx.globalCompositeOperation = "destination-in"
    ctx.drawImage(brushCanvas, 0, 0, cnvs.width, cnvs.height)

    return cnvs.toDataURL("image/jpeg")
  }

  const generate = async () => {
    try {
      setIsLoading(true)
      if (!userData) {
        return
      }

      const presetValues = Object.values(selectedPresets)
        .map((preset) => preset.value)
        .join(", ")

      const finalPrompt = userPrompt
        ? `${userPrompt}${presetValues ? `, ${presetValues}` : ""}`
        : presetValues || selectedPreset?.value || ""

      const res = await fetch(
        "https://py-temp-production.up.railway.app/gen/start",
        {
          method: "POST",
          body: JSON.stringify({
            preset: finalPrompt,
            mask: getBase64InpaintMask(),
            image: inputImageBase64,
          }),
          headers: {
            Authorization: userData.id,
            "Content-Type": "application/json",
          },
        }
      )

      const data = await res.json()
      console.log("Generation started with job_id:", data.image_id)

      const intervalId = setInterval(async () => {
        const statusRes = await fetch(
          `https://py-temp-production.up.railway.app/gen/status/${data.image_id}`,
          {
            headers: {
              Authorization: "h5yNyShnuXdwnuzgDNHAFkucFNB6x0PF",
              "Content-Type": "application/json",
            },
          }
        )

        const statusData = await statusRes.json()

        if (statusData.status === "completed") {
          clearInterval(intervalId)
          setOutputImageBase64(statusData.base64_data)
          setIsLoading(false)

          await fetchUserData()
        }
      }, 2000)
    } catch (error) {
      console.error("Generation error:", error)
      toast.error(
        "Generating prohibited content is not allowed on our platform try a different image."
      )
      setIsLoading(false)
    }
  }

  const reset = () => {
    setInputImageBase64(null)
    setOutputImageBase64(null)
    setCustomFaceImageBase64(null)
    setIsCustomFaceMode(false)
    setIsMasked(false)
    resetCanvas()
  }

  return {
    inputImageBase64,
    setInputImageBase64,
    inputImageName,
    setInputImageName,
    imageNaturalSize,
    setImageNaturalSize,
    outputImageBase64,
    setOutputImageBase64,
    customFaceImageBase64,
    setCustomFaceImageBase64,
    customFaceImageBase64Name,
    setCustomFaceImageBase64Name,
    isCustomFaceMode,
    setIsCustomFaceMode,
    isLoading,
    setIsLoading,
    selectedPreset,
    setSelectedPreset,
    selectedPresets,
    updatePreset,
    clearSelectedPresets,
    userPrompt,
    setUserPrompt,
    showHowToUse,
    setShowHowToUse,
    generationTime,
    setGenerationTime,
    isMasked,
    generate,
    reset,
  }
})

export const CreateGenerationProvider = CreateGenerationContainer.Provider
export const useCreateGeneration = CreateGenerationContainer.useContainer
